import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core'
import { every, isArray, some } from 'lodash'
import { combineLatestWith, first, map, Subject, takeUntil } from 'rxjs'

import { ClinicModulesKeys } from '../model/clinic'
import { ClinicService } from '../services/clinic.service'
import { PermissionsService } from '../services/permissions.service'

type Module = ClinicModulesKeys

const appModules = Object.values(ClinicModulesKeys) as string[]

@Directive({
  selector: '[mmxHasAccess]',
  standalone: false,
})
export class HasAccessDirective implements OnInit, OnDestroy {
  @Input()
  public mmxHasAccess: string | string[] | Module | Module[]

  @Input()
  public mmxHasAccessModule: Module | Module[]

  @Input()
  public mmxHasAccessPermission: string | string[]

  @Input()
  public mmxHasAccessElse: TemplateRef<any> | null = null

  @Input()
  public mmxHasAccessOperator: 'AND' | 'IN' | 'OR' = 'AND'

  @Input()
  public mmxHasAccessModuleOperator: 'AND' | 'IN' | 'OR'

  @Input()
  public mmxHasAccessPermissionOperator: 'AND' | 'IN' | 'OR'

  private onDestroy$ = new Subject<void>()

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private clinicService: ClinicService,
    private permissionsSvc: PermissionsService,
  ) { }

  public ngOnInit() {
    const array = this.mmxHasAccess ? (isArray(this.mmxHasAccess) ? this.mmxHasAccess : [this.mmxHasAccess]) : []
    const modules = []
    const permissions = []

    for (const item of array) {
      if (appModules.includes(item)) {
        modules.push(item)
      } else {
        permissions.push(item)
      }
    }

    if (this.mmxHasAccessModule) {
      modules.push(...isArray(this.mmxHasAccessModule) ? this.mmxHasAccessModule : [this.mmxHasAccessModule])
    }

    if (this.mmxHasAccessPermission) {
      permissions.push(...isArray(this.mmxHasAccessPermission) ? this.mmxHasAccessPermission : [this.mmxHasAccessPermission])
    }

    const modulesOperator = this.mmxHasAccessModuleOperator ?? this.mmxHasAccessOperator
    const permissionsOperator = this.mmxHasAccessPermissionOperator ?? this.mmxHasAccessOperator

    this.clinicService.clinic$.pipe(
      takeUntil(this.onDestroy$),
      combineLatestWith(
        this.permissionsSvc.hasPermission(permissions, permissionsOperator).pipe(
          takeUntil(this.onDestroy$),
          first(),
        ),
      ),
      first(),
      map(([clinic, hasPermissions]) => {
        if (!hasPermissions) {
          return false
        }

        if (modulesOperator === 'AND') {
          return every(modules, module => clinic.modules[module])
        } else {
          return some(modules, module => clinic.modules[module])
        }
      }),
    ).subscribe((hasModule) => this.handleVisibility(hasModule))
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  private handleVisibility(hasModule: boolean) {
    this.viewContainer.clear()

    if (hasModule) {
      this.viewContainer.createEmbeddedView(this.templateRef)
    } else if (this.mmxHasAccessElse) {
      this.viewContainer.createEmbeddedView(this.mmxHasAccessElse)
    }
  }
}
